import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { UiIcon } from '../components/ui-icon';
import { useI18n } from '../i18n/i18n-provider';
import { useSession } from '../session/session-provider';
import { adminWorkspaceActions } from '../admin-workspace-actions';
import { useOrganizationBranding } from '../organization-branding-provider';

/** All organization profiles keep the same permission-driven navigation axis. */
export function AdminWorkspaceNavigation({ masthead = false }: { readonly masthead?: boolean }) {
  const { t } = useI18n();
  const actions = adminWorkspaceActions(useSession());
  const { active } = useOrganizationBranding();
  const template = active?.templateId ?? 'standard';
  const themed = template !== 'standard';
  const settingsRoutes = ['/admin/event-types', '/admin/organization/privacy', '/admin/organization/branding', '/admin/notifications'];
  const mainActions = themed ? actions.filter((action) => !settingsRoutes.includes(action.to)) : actions;
  const settingsActions = themed ? actions.filter((action) => settingsRoutes.includes(action.to)) : [];
  const location = useLocation();
  const settingsActive = settingsActions.some((action) => location.pathname.startsWith(action.to));

  // Older callers place Editorial navigation in the header. The rail now owns it.
  if (masthead) return null;

  return <nav className={`surface__nav workspace-frame__nav${themed ? ' workspace-frame__nav--guided' : ''}`} aria-label={t('nav.admin')}>
          <NavLink to="/admin" end className="surface__nav-link">
            <span className="surface__nav-link-label">
              <span className="surface__nav-link-icon"><UiIcon name="spark" className="feature-card__icon-svg" /></span>
              {t('nav.overview')}
            </span>
          </NavLink>
          {mainActions.map((action) => <NavLink key={action.to} to={action.to} end={action.to === '/admin/organization'} className="surface__nav-link">
            <span className="surface__nav-link-label">
              <span className="surface__nav-link-icon"><UiIcon name={action.icon} className="feature-card__icon-svg" /></span>
              {t(action.titleKey)}
            </span>
          </NavLink>)}
          {settingsActions.length > 0 && <details className="workspace-settings-nav" key={`${template}-${location.pathname}`} open={settingsActive}>
            <summary className={`surface__nav-link${settingsActive ? ' active' : ''}`}><span className="surface__nav-link-label"><span className="surface__nav-link-icon"><UiIcon name="flow" className="feature-card__icon-svg" /></span>{t('workspace.settings')}</span></summary>
            <div className="workspace-settings-nav__links">{settingsActions.map((action) => <NavLink key={action.to} to={action.to} className="surface__nav-link">
              <span className="surface__nav-link-label"><span className="surface__nav-link-icon"><UiIcon name={action.icon} className="feature-card__icon-svg" /></span>{t(action.titleKey)}</span>
            </NavLink>)}</div>
          </details>}
        </nav>;
}

export function AdminLayout() {
  const { t } = useI18n();
  const { active } = useOrganizationBranding();
  const template = active?.templateId ?? 'standard';
  const themed = template !== 'standard';
  return (
    <section className={`workspace-frame surface surface--admin${themed ? ` workspace-frame--themed workspace-frame--guided workspace-frame--${template}` : ''}`} data-surface="admin"
      data-workspace-pattern={themed ? 'context-axis' : undefined}>
      <aside className="workspace-frame__rail">
        <div className="workspace-frame__intro">
          <span className="workspace-frame__intro-mark"><UiIcon name="scanner" className="feature-card__icon-svg" /></span>
          <span className="page__eyebrow">{t('nav.admin')}</span>
          <strong className="workspace-frame__title">{t('admin.title')}</strong>
          <p className="text-muted">{t('admin.workspace.intro')}</p>
        </div>
        <AdminWorkspaceNavigation />
        {themed && <a className="workspace-frame__signature" href="https://vcode.zeleznalady.cz" target="_blank" rel="noopener noreferrer">{t('workspace.poweredBy')}</a>}
      </aside>
      <div className="workspace-frame__content"><Outlet /></div>
    </section>
  );
}
